import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Track } from '@app/core/models/Track';
import { HotkeysService } from '@app/core/services/hotkeys.service';
import { PlayAudio } from '@app/core/states/audio.actions';
import { AudioState } from '@app/core/states/audio.state';
import { AuthState } from '@app/core/states/auth.state';
import { LiveTrackingState } from '@app/core/states/live-tracking.state';
import { RadioState } from '@app/core/states/radio.state';
import { LibraryAudioLineComponent } from '@app/library/components/content/library-audio-line/library-audio-line.component';
import { PlaylistAudioLineComponent } from '@app/library/components/content/playlist-audio-line/playlist-audio-line.component';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import { BacsState } from '@app/library/states/bacs.state';
import { ColumnTypes } from '@app/library/states/columns.actions';
import {
  ResetEditingTracks,
  StartEditMultipleTracks,
  StartEditTrack,
} from '@app/library/states/editing-track.actions';
import {
  SelectMultipleTracks,
  SelectTrack,
  ToggleSelectPlayTrack,
  ToggleSelectTrack,
  UnselectAll,
  UnselectTrack,
} from '@app/library/states/selected-tracks.actions';
import { SelectedTracksState } from '@app/library/states/selected-tracks.state';
import { TrackCuesRequest } from '@app/library/states/track-mix-points.actions';
import {
  TracksAddToPlaylistSuccess,
  TracksTagsSaveSuccess,
} from '@app/library/states/tracks.actions';
import { TracksState } from '@app/library/states/tracks.state';
import { MIXPOINT_MODAL_ID, SavedToLocalstorage } from '@app/shared/constants';
import {
  sortDate,
  sortDateReverse,
  sortNumber,
  sortNumberReverse,
  sortString,
  sortStringReverse,
} from '@app/shared/utils';
import { environment } from '@env/environment';
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
import {
  Actions,
  ofActionDispatched,
  ofActionSuccessful,
  Select,
  Store,
} from '@ngxs/store';
import { ButtonComponent } from '@radioking/ui/button';
import amplitude from 'amplitude-js';
import { BehaviorSubject, fromEvent, Observable, Subject, Subscription } from 'rxjs';
import {
  delay,
  filter,
  map,
  pairwise,
  startWith,
  take,
  tap,
  throttleTime,
} from 'rxjs/operators';

const mResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

export type SortPredicate =
  | 'position'
  | 'title'
  | 'artist'
  | 'album'
  | 'timeSeconds'
  | 'uploadDate'
  | 'bitrate'
  | 'bpm'
  | 'none';

export interface TableSortPreferences {
  predicate: SortPredicate;
  reverse: boolean;
}

@Directive() // Needed by angular, but conflicts with @Dispatch() decorator
export abstract class TracksTableDirective<T extends Track = Track>
  implements AfterViewInit, OnDestroy
{
  @Input()
  set tracks(tracksInput: T[]) {
    this.loadPreferredTableSort();

    if (tracksInput) {
      this._originalTracks = tracksInput;
      this.tracksObservable$.next(tracksInput);
      this.sortAll();
      this.detectScroll();
    }
  }

  get tracks(): T[] {
    return this._originalTracks || [];
  }

  _originalTracks: T[];

  subscription = new Subscription();
  trackOpen: T = null;
  predicate: SortPredicate = 'uploadDate';
  reverse = false;
  lastSelectedTrackId = -1;
  hasScroll = false;
  tracksObservable$: BehaviorSubject<T[]> = new BehaviorSubject([]);
  tagMaxWidth = 85;
  tagDisplayedLimiter = 0;
  lastArrow = '';
  selectedWithShift: number[] = [];
  hiddenActions: string[] = [];
  remainingSpaceChecked = true;
  resizeObserver: ResizeObserver;
  resizeObservable = new Subject<number>();
  readonly preferredTableSortStorageKey: SavedToLocalstorage =
    SavedToLocalstorage.PREFERENCE_TABLE_SORT_LIBRARY_AND_TAG;

  @ViewChild(CdkVirtualScrollViewport) cdkTable: CdkVirtualScrollViewport;
  @ViewChild('table') table: ElementRef<HTMLElement>;
  @ViewChild('tagRef') tagZone: ElementRef;
  @ViewChild('playlistRef') playlistZone: ElementRef;
  @ViewChild('audioLine') firstAudioLine:
    | LibraryAudioLineComponent
    | PlaylistAudioLineComponent;
  @ViewChildren('headerAction')
  headerActions: QueryList<ButtonComponent>;
  @Output() readonly columnsToggle = new EventEmitter<ColumnTypes>();

  @Select(AudioState.currentTrackPlaying)
  currentTrackPlaying$: Observable<Track>;

  @Select(AudioState.isPlaying)
  isPlaying$: Observable<boolean>;

  protected constructor(
    protected readonly store: Store,
    protected readonly modalService: LibraryModalService,
    protected readonly actions$: Actions,
    protected readonly dialog: MatDialog,
    protected readonly hotkeys: HotkeysService,
    protected readonly cdr: ChangeDetectorRef,
  ) {
    this.subscription.add(
      fromEvent(window, 'resize')
        .pipe(throttleTime(250))
        .subscribe(() => this.onWindowResize()),
    );
  }

  abstract archiveTrack(track: T, shortcut: boolean): void;
  abstract archiveMultiple(shortcut: boolean): void;
  abstract detectScroll(): void;

  ngAfterViewInit(): void {
    this.updateTagZoneSize();
    this.detectScroll();
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(ToggleSelectTrack, ToggleSelectPlayTrack))
        .subscribe(({ clicked }: ToggleSelectTrack) => {
          if (clicked) {
            this.selectedWithShift = [];
          }
        }),
    );

    // Hotkeys
    this.subscription.add(
      this.hotkeys.fromConfigArray([
        { hotkey: 'delete', handler: () => this.onDeleteHotkey() },
        { hotkey: 'selectAll', handler: () => this.onSelectAllHotkey() },
        { hotkey: 'edit', handler: () => this.onEditHotkey() },
        { hotkey: 'selectUp', handler: e => this.onSelectUpDownHotkey(e) },
        { hotkey: 'selectDown', handler: e => this.onSelectUpDownHotkey(e) },
        {
          hotkey: 'selectUpWithSelection',
          handler: e => this.onSelectUpDownWithSelectionHotkey(e),
        },
        {
          hotkey: 'selectDownWithSelection',
          handler: e => this.onSelectUpDownWithSelectionHotkey(e),
        },
        { hotkey: 'playPause', handler: () => this.onPlayPauseHotkey() },
        { hotkey: 'cancel', handler: () => this.onCancelHotkey() },
      ]),
    );

    this.subscription.add(
      this.tracksObservable$.pipe(startWith([]), pairwise()).subscribe(([prev, next]) => {
        if (next.length && !prev.length) {
          this.resizeObserver = new mResizeObserver((entries: ResizeObserverEntry[]) => {
            this.resizeObservable.next(entries[0].target.getBoundingClientRect().width);
          });
          setTimeout(() => {
            if (this.table?.nativeElement) {
              this.resizeObserver.observe(this.table.nativeElement);
            }
          }, 250);
        } else if (!next.length && prev.length) {
          if (this.table?.nativeElement) {
            this.resizeObserver.unobserve(this.table.nativeElement);
          }
        }
      }),
    );

    this.subscription.add(
      this.actions$
        .pipe(ofActionSuccessful(TracksTagsSaveSuccess, TracksAddToPlaylistSuccess))
        .subscribe(() => {
          this.cdr.detectChanges();
        }),
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(new ResetEditingTracks());
    this.subscription.unsubscribe();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onWindowResize(): void {
    this.updateTagZoneSize();
    this.detectScroll();
  }

  selectedTracksObserver(count$: Observable<number>) {
    this.subscription.add(
      count$
        .pipe(
          startWith(0),
          pairwise(),
          filter(([, count]) => count > 0),
          tap(([prev]) => {
            if (prev === 0) {
              this.remainingSpaceChecked = false;
            }
          }),
          delay(1),
        )
        .subscribe(() => {
          this.updateHeaderActions();
        }),
    );
  }

  updateHeaderActions() {
    if (this.table?.nativeElement) {
      const actionsArray = this.headerActions.toArray();
      actionsArray.forEach(btn => {
        const btnHTML = btn.getButton();
        if (btn.getButton().style.display === 'none') {
          btnHTML.style.display = 'block';
          btnHTML.style.opacity = '0';
        }
      });

      actionsArray.reverse().forEach(btn => {
        const btnHTML = btn.getButton();
        const remainingSpace = window.innerWidth - btnHTML.getBoundingClientRect().right;

        const btnAction = btnHTML.getAttribute('data-action');
        if (remainingSpace < 80) {
          if (this.hiddenActions.indexOf(btnAction) < 0) {
            this.hiddenActions.push(btnAction);
          }
          btnHTML.style.display = 'none';
        } else {
          if (this.hiddenActions.indexOf(btnAction) > -1) {
            this.hiddenActions.splice(this.hiddenActions.indexOf(btnAction), 1);
          }
          btnHTML.style.opacity = '1';
        }
      });
      if (!this.hasTags() && this.hiddenActions.indexOf('remove-tags') > -1) {
        this.hiddenActions.splice(this.hiddenActions.indexOf('remove-tags'), 1);
      }
      this.remainingSpaceChecked = true;
    }
  }

  editMultipleTracks(shortcut = false) {
    const selected = this.store.selectSnapshot(SelectedTracksState.selectedTracks);
    if (!selected.length) {
      return;
    }

    let tracks: T[] = [];
    selected.map(id =>
      this.store
        .select(TracksState.trackWithId)
        .pipe(
          map(fn => fn(id)),
          take(1),
        )
        .subscribe(track => {
          if (track && !track.locked) {
            tracks = [...tracks, track as T];
          }
        }),
    );
    if (tracks.length === 1) {
      this.editTrack(tracks[0], true);
    } else if (tracks.length >= 2) {
      amplitude
        .getInstance()
        .logEvent(shortcut ? 'open_track_edition_shortcut' : 'open_track_edition');
      this.store.dispatch(new StartEditMultipleTracks(tracks));
    }
  }

  removeTagsFromTracks(track: T = null) {
    if (track) {
      this.modalService.removeTagsFromTracks([track]);
    } else {
      this.modalService.removeTagsFromTracks(
        this.store.selectSnapshot(SelectedTracksState.selectedTracksObjects),
      );
    }
  }

  toggleAll(e: MatCheckboxChange, force = false) {
    if (force || e.checked) {
      return this.store.dispatch(new SelectMultipleTracks(this.tracks));
    } else {
      return this.store.dispatch(new UnselectAll());
    }
  }

  isMultiSelect(id: number) {
    const selectedTracks = this.store.selectSnapshot(SelectedTracksState.selectedTracks);

    return selectedTracks.length > 1 && selectedTracks.findIndex(t => t === id) > -1;
  }

  getMultiSelectButtonStyle(): any {
    return {
      padding: '0 10px',
      height: '18px',
    };
  }

  canDownloadTrack = (track: T) => {
    const bacOfTrack = this.store.selectSnapshot(BacsState.rootBacById)(track.idTrackbox);

    return (
      bacOfTrack?.type !== 'chronic' ||
      this.store.selectSnapshot(LiveTrackingState.canDownloadChronics)
    );
  };

  isNotLocked(track: T) {
    return !track.locked;
  }

  tracksLocked(track: T = null): boolean {
    if (track) {
      return track.locked;
    }

    return this.store
      .selectSnapshot(SelectedTracksState.selectedTracksObjects)
      .every(t => t.locked);
  }

  hasTags(track: T = null): boolean {
    if (track) {
      return !!track.tags.length;
    }

    return this.store
      .selectSnapshot(SelectedTracksState.selectedTracksObjects)
      .some(t => t.tags.length > 0);
  }

  canRemoveTagsFromTracks = (track: T) => this.isNotLocked(track) && this.hasTags(track);

  addTagsToTrack(track: T = null) {
    if (track) {
      this.modalService.addTagsToTrack([track.id]);
    } else {
      this.modalService.addTagsToTrack(
        this.store.selectSnapshot(SelectedTracksState.selectedTracks),
      );
    }
  }

  addTracksToPlaylist() {
    const selectedTracks = this.store.selectSnapshot(SelectedTracksState.selectedTracks);
    this.modalService.addToPlaylist(selectedTracks);
  }

  playTrack(track: Track) {
    this.currentTrackPlaying$.pipe(take(1)).subscribe(currentTrackPlaying => {
      if (currentTrackPlaying?.id !== track.id) {
        this.store.dispatch(new PlayAudio(track));
      }
    });
  }

  editMixPointTrack(track: T) {
    this.store.dispatch(new TrackCuesRequest(track));
    this.modalService.editMixPoints(track);
  }

  downloadTrack(track: T) {
    const radioId = this.store.selectSnapshot(RadioState.currentRadioId);
    const token = this.store.selectSnapshot(AuthState.getAccessToken);
    window.open(
      `${environment.urls.MAIN_API_V2}/radio/${radioId}/track/${track.id}/download?token=${token}`,
    );
  }

  addTrackToPlaylist(track: T) {
    this.modalService.addToPlaylist([track.id]);
  }

  onOpenContextMenu(track: T) {
    this.trackOpen = track;
  }

  onCloseContextMenu() {
    this.trackOpen = null;
  }

  updateTagZoneSize() {
    const tagZone = this.tagZone ?? this.firstAudioLine?.tagZone;
    const playlistZone = this.playlistZone ?? this.firstAudioLine?.playlistZone;

    if (tagZone || playlistZone) {
      this.tagDisplayedLimiter = Math.floor(
        ((tagZone
          ? tagZone.nativeElement.offsetWidth
          : playlistZone.nativeElement.offsetWidth) -
          10) /
          (this.tagMaxWidth + 5),
      );
    }
  }

  requestToggleColumn(column: ColumnTypes): void {
    this.columnsToggle.emit(column);
    this.updateTagZoneSize();
  }

  sortAll() {
    this.sortBy(this.predicate, !this.reverse);
  }

  getPreferredTableSort(): TableSortPreferences | null {
    const prefs = localStorage.getItem(this.preferredTableSortStorageKey);

    if (typeof prefs === 'string') {
      return JSON.parse(prefs) as TableSortPreferences;
    }

    return prefs;
  }

  setPreferredTableSort(prefs: TableSortPreferences) {
    localStorage.setItem(this.preferredTableSortStorageKey, JSON.stringify(prefs));
  }

  loadPreferredTableSort() {
    const tableSortPreferences = this.getPreferredTableSort();

    if (tableSortPreferences) {
      this.predicate = tableSortPreferences.predicate;
      this.reverse = tableSortPreferences.reverse;
    }
  }

  getSortedArray(prop: SortPredicate, revert = false): T[] {
    if (prop === 'none') {
      return this.tracks;
    }
    if (prop === 'uploadDate') {
      if (revert) {
        return this.tracks.slice().sort(sortDateReverse(prop));
      }

      return this.tracks.slice().sort(sortDate(prop));
    }
    if (['title', 'artist', 'album'].includes(prop)) {
      if (revert) {
        return this.tracks.slice().sort(sortStringReverse(prop));
      }

      return this.tracks.slice().sort(sortString(prop));
    }
    if (revert) {
      return this.tracks.slice().sort(sortNumberReverse(prop));
    }

    return this.tracks.slice().sort(sortNumber(prop));
  }

  sortBy(prop: SortPredicate, revert = false) {
    // We do two case since if we add a if in the sort function, we loose performance
    this.tracksObservable$.next(this.getSortedArray(prop, revert));
  }

  transition() {
    this.setPreferredTableSort({ predicate: this.predicate, reverse: this.reverse });
    this.sortAll();
  }

  onSelectShiftLine(idTrackTriggered: number) {
    if (this.lastSelectedTrackId === -1) {
      return;
    }
    this.tracksObservable$
      .asObservable()
      .pipe(take(1))
      .subscribe(tracks => {
        const startIdx = tracks.findIndex(track => track.id === this.lastSelectedTrackId);
        const endIdx = tracks.findIndex(track => track.id === idTrackTriggered);
        if (endIdx === startIdx) {
          return;
        }
        let ids: T[];
        if (endIdx < startIdx) {
          ids = tracks.slice(endIdx, startIdx + 1);
        } else {
          ids = tracks.slice(startIdx, endIdx + 1);
        }
        this.store.dispatch(new SelectMultipleTracks(ids.filter(tra => !tra.processing)));
        this.lastSelectedTrackId = idTrackTriggered;
      });
  }

  editTrack(track: Track, shortcut = false) {
    amplitude
      .getInstance()
      .logEvent(shortcut ? 'open_track_edition_shortcut' : 'open_track_edition');

    return this.store.dispatch(new StartEditTrack(track));
  }

  onDeleteHotkey() {
    if (this.trackOpen && !this.actionMultipleTrack()) {
      this.archiveTrack(this.trackOpen, true);
    } else if (this.store.selectSnapshot(SelectedTracksState.selectedTracks).length) {
      this.archiveMultiple(true);
    }
  }

  onSelectAllHotkey() {
    this.toggleAll(null, true);
  }

  onEditHotkey() {
    if (this.trackOpen && !this.actionMultipleTrack()) {
      this.editTrack(this.trackOpen, true);
    } else {
      this.editMultipleTracks(true);
    }
  }

  onSelectUpDownHotkey(e: KeyboardEvent) {
    if (this.lastSelectedTrackId === -1) {
      if (e.key === 'ArrowDown') {
        this.selectFirstTrack();
      }

      return;
    }
    this.selectNextTrack(e.key);
  }

  onSelectUpDownWithSelectionHotkey(e: KeyboardEvent) {
    if (this.lastSelectedTrackId === -1) {
      return;
    }
    this.selectNextTrack(e.key, true);
  }

  selectFirstTrack() {
    this.tracksObservable$
      .asObservable()
      .pipe(take(1))
      .subscribe(tracks => {
        if (!tracks.length) {
          return;
        }
        const focusedTrack = tracks[0];
        this.store.dispatch(new SelectTrack(focusedTrack));
      });
  }

  selectNextTrack(key: string, shiftPressed = false) {
    this.tracksObservable$
      .asObservable()
      .pipe(take(1))
      .subscribe(tracks => {
        const startIdx = tracks.findIndex(track => track.id === this.lastSelectedTrackId);
        let newIdx =
          this.lastArrow !== '' && key !== this.lastArrow && shiftPressed
            ? startIdx
            : startIdx + (key === 'ArrowDown' ? 1 : -1);
        if (newIdx < 0 || newIdx >= tracks.length) {
          this.checkTableScroll(newIdx, key);

          return;
        }
        let newFocusedTrack = tracks[newIdx];

        if (!shiftPressed) {
          this.store.dispatch(new UnselectAll());
          this.store.dispatch(new SelectTrack(newFocusedTrack, true));
          this.lastArrow = '';
          this.selectedWithShift = [];
          this.checkTableScroll(newIdx, key);

          return;
        }

        if (
          newFocusedTrack.id ===
          this.store.selectSnapshot(SelectedTracksState.lastClicked)
        ) {
          newIdx = newIdx + (key === 'ArrowDown' ? 1 : -1);
          newIdx = Math.min(tracks.length - 1, Math.max(0, newIdx));
          newFocusedTrack = tracks[newIdx];
        }
        this.checkTableScroll(newIdx, key);

        if (this.selectedWithShift.indexOf(newFocusedTrack.id) > -1) {
          this.store.dispatch(new UnselectTrack(newFocusedTrack));
          this.selectedWithShift = this.selectedWithShift.filter(
            id => id !== newFocusedTrack.id,
          );
        } else {
          this.store.dispatch(new SelectTrack(newFocusedTrack));
          this.selectedWithShift.push(newFocusedTrack.id);
        }
        this.lastSelectedTrackId = newFocusedTrack.id;
        this.lastArrow = key;
      });
  }

  checkTableScroll(idx: number, key: string) {
    const linesDisplayed = Math.floor(
      this.cdkTable.elementRef.nativeElement.offsetHeight / 50,
    );
    if (
      key === 'ArrowDown' &&
      (idx + 1) * 50 > this.cdkTable.measureScrollOffset('top') + linesDisplayed * 50
    ) {
      this.cdkTable.scrollToIndex(1 + idx - linesDisplayed);
    } else if (this.cdkTable.measureScrollOffset('top') > idx * 50) {
      this.cdkTable.scrollToIndex(idx);
    }
  }

  onPlayPauseHotkey() {
    if (this.dialog.getDialogById(MIXPOINT_MODAL_ID)) {
      return;
    }
    amplitude.getInstance().logEvent('listen_track_shortcut');
    if (this.trackOpen) {
      this.playTrack(this.trackOpen);

      return;
    }
    const selectedIds = this.store.selectSnapshot(SelectedTracksState.selectedTracks);
    if (selectedIds.length === 1) {
      const selectedTrack = this.store.selectSnapshot(TracksState.trackWithId)(
        selectedIds[0],
      );
      this.playTrack(selectedTrack);
    }
    const selectedPlayTracks = this.store.selectSnapshot(
      SelectedTracksState.selectedPlayTracks,
    );
    if (selectedPlayTracks.length === 1) {
      this.playTrack(selectedPlayTracks[0]);
    }
  }

  onCancelHotkey() {
    this.store.dispatch(new UnselectAll());
  }

  actionMultipleTrack() {
    const selectedTracks = this.store.selectSnapshot(SelectedTracksState.selectedTracks);
    const selectedPlayTracks = this.store.selectSnapshot(
      SelectedTracksState.selectedPlayTracks,
    );

    return (
      !this.trackOpen ||
      (selectedTracks.length > 1 &&
        selectedTracks.filter(id => id === this.trackOpen.id).length) ||
      (selectedPlayTracks.length > 1 &&
        selectedPlayTracks.filter(t => t.id === this.trackOpen.id).length)
    );
  }

  showOnHeaderContextMenu(action: string) {
    return this.hiddenActions.indexOf(action) > -1;
  }
}
